// Cobrança PIX de um VALOR (Mesas): gera o QR pelo endpoint de cobrança
// avulsa, pede CPF quando a loja exige, faz polling até pagar/expirar e
// avisa quem chamou. Não mexe no pedido — quem fecha a mesa é a tela.
//
// Por que não reaproveitar o PixOnlineModal: ele é do PEDIDO (cria o PIX
// pelo id do pedido, com o total do pedido, e o backend força o pedido pra
// "aguardando aceite" e cancela se expirar). Numa mesa isso cancelava o
// pedido da mesa em 30 s e cobrava o total em vez do que faltava.

import { CheckCircle, Copy, QrCode, RefreshCw, Timer } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { pixCobrancaApi } from "./api";
import { formatarCpf, cpfValido } from "./lib/cpf";

const POLL_MS = 4000;
const fmt = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;

export default function PixCobrancaModal({ open, onClose, pedidoId, amount, nomeCliente, onPago }) {
  const [etapa, setEtapa] = useState("gerando"); // gerando | cpf | qr | pago | expirado | erro
  const [tx, setTx] = useState(null);
  const [cpf, setCpf] = useState("");
  const [nome, setNome] = useState(nomeCliente || "");
  const [erro, setErro] = useState("");
  const [restante, setRestante] = useState(0);
  const pollRef = useRef(null);
  const pagoRef = useRef(false);

  const pararPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const gerar = useCallback(async (cpfInformado) => {
    setEtapa("gerando"); setErro("");
    try {
      const res = await pixCobrancaApi.criar({ pedido_id: pedidoId, amount, cpf: cpfInformado || undefined, nome: nome || undefined });
      setTx(res.data); setEtapa("qr");
    } catch (e) {
      if (e?.response?.status === 409 && e.response?.headers?.["x-error-code"] === "CPF_REQUIRED") { setEtapa("cpf"); return; }
      setErro(e?.response?.data?.detail || "Não foi possível gerar o PIX."); setEtapa("erro");
    }
  }, [pedidoId, amount, nome]);

  // Abrir: zera e gera (sem CPF; o backend diz se precisa)
  useEffect(() => {
    if (!open) { pararPolling(); return; }
    pagoRef.current = false; setTx(null); setCpf(""); setNome(nomeCliente || "");
    gerar("");
    return pararPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Polling + contagem enquanto o QR está na tela
  useEffect(() => {
    if (etapa !== "qr" || !tx?.id) return;
    const tick = async () => {
      try {
        const r = await pixCobrancaApi.status(tx.id);
        const st = r.data?.status;
        if (st === "approved" && !pagoRef.current) {
          pagoRef.current = true; pararPolling(); setEtapa("pago");
          toast.success("Pagamento PIX confirmado!");
          setTimeout(() => onPago?.(r.data), 900);
        } else if (["expired", "rejected", "cancelled"].includes(st)) { pararPolling(); setEtapa("expirado"); }
      } catch (_) { /* rede: tenta de novo no próximo tick */ }
    };
    pollRef.current = setInterval(tick, POLL_MS);
    const cont = setInterval(() => {
      if (!tx.expires_at) return;
      const s = Math.max(0, Math.floor((new Date(tx.expires_at) - Date.now()) / 1000));
      setRestante(s);
      if (s === 0 && !pagoRef.current) { setEtapa("expirado"); pararPolling(); }
    }, 1000);
    return () => { pararPolling(); clearInterval(cont); };
  }, [etapa, tx, onPago]);

  const copiar = async () => {
    try { await navigator.clipboard.writeText(tx.qr_code); toast.success("Código PIX copiado!"); }
    catch (_) { toast.error("Não foi possível copiar."); }
  };
  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="w-[95vw] sm:max-w-md" data-e2e="dialog-pix-cobranca">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <QrCode className="w-5 h-5 text-primary" /> PIX Online · {fmt(amount)}
          </DialogTitle>
        </DialogHeader>

        {etapa === "gerando" && (
          <div className="py-10 text-center text-sm text-muted-foreground">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3" />
            Gerando código PIX…
          </div>
        )}

        {etapa === "cpf" && (
          <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); if (!cpfValido(cpf)) { setErro("CPF inválido."); return; } gerar(cpf.replace(/\D/g, "")); }}>
            <p className="text-sm text-muted-foreground">O PIX precisa do CPF de quem vai pagar.</p>
            <div>
              <label className="text-sm font-medium" htmlFor="pix-cpf">CPF</label>
              <Input id="pix-cpf" inputMode="numeric" autoFocus value={cpf} onChange={(e) => { setCpf(formatarCpf(e.target.value)); setErro(""); }} placeholder="000.000.000-00" className="mt-1 h-12 text-lg tracking-wider" />
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="pix-nome">Nome (opcional)</label>
              <Input id="pix-nome" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome do cliente" className="mt-1 h-11" />
            </div>
            {erro && <p className="text-sm text-destructive">{erro}</p>}
            <Button type="submit" className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground" data-e2e="pix-cpf-gerar">Gerar QR Code</Button>
          </form>
        )}

        {etapa === "qr" && tx && (
          <div className="space-y-3">
            <div className="flex items-center justify-center gap-2 text-sm rounded-lg border border-primary/30 bg-primary/5 py-2 text-primary">
              <Timer className="w-4 h-4" /> Aguardando pagamento · {tx.expires_at ? mmss(restante) : "verificando…"}
            </div>
            {tx.qr_code_base64 && (
              <div className="bg-white p-3 rounded-lg border text-center">
                <img src={`data:image/jpeg;base64,${tx.qr_code_base64}`} alt="QR Code PIX" className="mx-auto" style={{ maxWidth: 220 }} />
              </div>
            )}
            {tx.qr_code && (
              <div className="flex gap-2">
                <input readOnly value={tx.qr_code} className="flex-1 px-3 h-11 border rounded-lg text-xs font-mono bg-muted/40" />
                <Button type="button" onClick={copiar} className="h-11 bg-primary hover:bg-primary/90 text-primary-foreground"><Copy className="w-4 h-4 mr-1" /> Copiar</Button>
              </div>
            )}
            <p className="text-center text-2xl font-bold text-primary">{fmt(amount)}</p>
          </div>
        )}

        {etapa === "pago" && (
          <div className="py-8 text-center space-y-2" data-e2e="pix-pago">
            <CheckCircle className="w-14 h-14 text-green-500 mx-auto" />
            <p className="text-xl font-bold">Pagamento confirmado!</p>
            <p className="text-sm text-muted-foreground">{fmt(amount)} recebido via PIX.</p>
          </div>
        )}

        {(etapa === "expirado" || etapa === "erro") && (
          <div className="py-6 text-center space-y-4">
            <p className="font-semibold">{etapa === "expirado" ? "Este PIX expirou sem pagamento." : "Não foi possível gerar o PIX."}</p>
            {erro && <p className="text-sm text-destructive">{erro}</p>}
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1 h-11" onClick={onClose}>Fechar</Button>
              <Button type="button" className="flex-1 h-11 bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => gerar(cpf.replace(/\D/g, ""))}>
                <RefreshCw className="w-4 h-4 mr-1" /> Gerar novo PIX
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
